import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NotificationSettingsState } from '../../@types/reduxStates';
import { APIError, APIStatus } from '../../@types/APIStatus';
import { NotificationSettingsResponse } from '../../@types/responsesAPI';
import { axiosInstance } from '../../utils/axiosConfig';
import {
  DialogNotificationData,
  NotificationSettingsRow
} from '../../@types/tableRows';
import { RootState } from '../rootReducer';

const initialState: NotificationSettingsState = {
  url: '',
  response: {
    rows: [],
    count: 0
  },
  APIStatus: APIStatus.IDLE
};

const slice = createSlice({
  name: 'notificationSettings',
  initialState,
  reducers: {
    setUrl: (
      state: NotificationSettingsState,
      action: PayloadAction<string>
    ) => {
      state.url = action.payload;
    },
    getNotificationSettings: (state: NotificationSettingsState) => {
      state.APIStatus = APIStatus.PENDING;
      state.error = undefined;
    },
    getNotificationSettingsSuccess: (
      state: NotificationSettingsState,
      action: PayloadAction<NotificationSettingsResponse>
    ) => {
      state.APIStatus = APIStatus.FULFILLED;
      state.response = action.payload;
    },

    getNotificationSettingsError: (
      state: NotificationSettingsState,
      action: PayloadAction<APIError>
    ) => {
      state.APIStatus = APIStatus.REJECTED;
      state.error = action.payload;
    }
  }
});

export function fetchNotificationSettings(
  params: Record<string, string>,
  signal: AbortSignal
) {
  return async (dispatch: any) => {
    try {
      dispatch(getNotificationSettings());
      params.lang = params.lang.split('-')[0] ?? 'de';

      const response = await axiosInstance.get('/api/v2/notificationSettings', {
        signal,
        params
      });

      dispatch(getNotificationSettingsSuccess(response.data));
    } catch (error) {
      dispatch(getNotificationSettingsError(error as APIError));
    }
  };
}

export async function executeNotificationSettings(id: number) {
  try {
    const response = await axiosInstance.post(
      `/api/v2/notificationSettings/play/${id}`
    );
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export function deleteNotificationSettings(id: number) {
  return async (dispatch: any, getState: () => RootState): Promise<boolean> => {
    try {
      const response = await axiosInstance.delete(
        `/api/v2/notificationSettings/delete/${id}`
      );
      const idRemoved = response.data.deleted;
      const state = getState();
      const filteredRows = state.notificationSettings.response.rows.filter(
        (r) => r.ruleId !== parseInt(idRemoved, 10)
      );
      const count = filteredRows.length;
      dispatch(getNotificationSettingsSuccess({ rows: filteredRows, count }));
      return true;
    } catch (error) {
      dispatch(getNotificationSettingsError(error as APIError));
      return false;
    }
  };
}

export function createNotificationSettings(
  row: NotificationSettingsRow,
  currentLang: string
) {
  const { emails, frequency, monthPeriod, time, weekDays, priority, active } =
    row.notificationConfig;
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(getNotificationSettings());
      const lang = currentLang.split('-')[0] ?? 'de';
      const response = await axiosInstance.post(
        '/api/v2/notificationSettings',
        {
          params: {
            cronJobId: row.cronJobId,
            isBase: row.isBase,
            weekDays: weekDays.map((wd) => wd).join(','),
            emails: emails.map((e) => e).join(','),
            time,
            monthPeriod,
            frequency,
            priority,
            active,
            lang
          }
        }
      );

      const state = getState();
      const originalRows = state.notificationSettings.response.rows;
      const originalIndex = originalRows.findIndex(
        (r) => r.ruleId === row.ruleId
      );
      const filteredRows = originalRows.filter((r) => r.ruleId !== row.ruleId);

      const newRuleId = response.data;
      const newRowConfig = {
        ...row,
        cronJobId: newRuleId.id,
        alertInterval: newRuleId.ruleReadable,
        isBase: false,
        priority,
        active
      };

      const resultRows = [...filteredRows];
      if (originalIndex !== -1) {
        resultRows.splice(originalIndex, 0, newRowConfig);
      } else {
        resultRows.unshift(newRowConfig);
      }
      dispatch(
        getNotificationSettingsSuccess({
          rows: resultRows,
          count: resultRows.length
        })
      );
    } catch (error) {
      dispatch(getNotificationSettingsError(error as APIError));
    }
  };
}

export function setActivation(row: NotificationSettingsRow) {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(getNotificationSettings());
      const response = await axiosInstance.post(
        '/api/v2/notificationSettings/activate',
        {
          params: {
            cronJobId: row.cronJobId,
            isBase: row.isBase,
            isActive: row.active
          }
        }
      );
      const state = getState();
      const originalRows = state.notificationSettings.response.rows;
      const originalIndex = originalRows.findIndex(
        (r) => r.ruleId === row.ruleId
      );
      const filteredRows = originalRows.filter((r) => r.ruleId !== row.ruleId);

      const { id } = response.data;
      const resultRows = [...filteredRows];
      if (originalIndex !== -1) {
        resultRows.splice(originalIndex, 0, {
          ...row,
          cronJobId: id,
          isBase: false
        });
      } else {
        resultRows.unshift({ ...row, cronJobId: id });
      }
      dispatch(
        getNotificationSettingsSuccess({
          rows: resultRows,
          count: resultRows.length
        })
      );
    } catch (error) {
      dispatch(getNotificationSettingsError(error as APIError));
    }
  };
}

/* export function saveNotificationSettings(params: DialogNotificationData) {
  const { emails, frequency, monthPeriod, ruleId, time, weekDays } = params;
  const method = ruleId ? 'put' : 'post';
  return async (dispatch: any) => {
    try {
      dispatch(getNotificationSettings());
      const response = await axiosInstance[method](
        '/api/v2/notificationSettings',
        {
          params: {
            weekDays: weekDays.map((wd) => wd).join(','),
            emails: emails.map((e) => e).join(','),
            ruleId,
            time,
            monthPeriod,
            frequency
          }
        }
      );
      const mergedData = [];
      dispatch(getNotificationSettingsSuccess(response.data));
    } catch (error) {
      dispatch(getNotificationSettingsError(error as APIError));
    }
  };
}
 */
// Reducer
export default slice.reducer;

// Actions
export const {
  getNotificationSettings,
  getNotificationSettingsSuccess,
  getNotificationSettingsError,
  setUrl
} = slice.actions;
